import { ReactElement } from 'react'
import * as React from 'react'
import Layout from '@components/organisms/Layouts/WithoutSidebar'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import RouteNavBar from '@components/pages/help-centre/RouteNavBar/RouteNavBar'
import Markdoc, { RenderableTreeNode } from '@markdoc/markdoc'
import {
  MDContainer,
  LastUpdateCard,
  PageContainer,
  PageContent,
  PageTitle,
  ArticleContent,
} from '@styled_components/helpCentre/styled.components'
import useWindowDimensions from '@hooks/useWindowDimensions'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'react-i18next'
import { Button as NButton } from '@nextui-org/react-latest'
import ArrowUpIcon from '@public/static/icons/arrowUp-icon'
import dynamic from 'next/dynamic'

const LoginAndSecurityIllustration = dynamic(
  () =>
    import('@public/static/vectorIllustrations/loginAndSecurityIllustration'),
  { ssr: false }
)

const AuthorProfileSettingsIllustration = dynamic(
  () =>
    import(
      '@public/static/vectorIllustrations/authorProfileSettingsIllustration'
    ),
  { ssr: false }
)

const VideoConfigHelpCentreIllustration = dynamic(
  () =>
    import(
      '@public/static/vectorIllustrations/videoConfigHelpCentreIllustration'
    ),
  { ssr: false }
)

type Category = 'student' | 'author' | 'referralPartner' | 'legal'

interface Article {
  articleName: string
  articlePath: string
  categoryName: string
  categoryPath: string
  lastUpdated?: string
  MDpath: string
  img?: ReactElement
}

const articles: Article[] = [
  {
    articleName: 'privacyPolicy',
    articlePath: 'privacy-policy',
    categoryName: 'Legal',
    categoryPath: 'legal',
    MDpath: '/static/markdown/privacy-policy.md',
    lastUpdated: '12 Oct 2023',
    img: <LoginAndSecurityIllustration />,
  },
  {
    articleName: 'termsAndConditions',
    articlePath: 'terms-and-conditions',
    categoryName: 'Legal',
    categoryPath: 'legal',
    MDpath: '/static/markdown/terms-and-conditions.md',
    lastUpdated: '18 Nov 2023',
    img: <AuthorProfileSettingsIllustration />,
  },
  {
    articleName: 'optimizeVideoForEdugram',
    articlePath: 'optimize-video-for-edugram',
    categoryName: 'Author',
    categoryPath: 'author',
    // MDpath: '/static/markdown/terms-and-conditions.md',
    MDpath: '/static/markdown/optimize-video-for-edugram.md',
    lastUpdated: '16 May 2024',
    img: <VideoConfigHelpCentreIllustration />,
  },
]

// custom divider for markdoc
const mdDivider = {
  render: 'MDDivider',
}

const markDocConfig = {
  tags: {
    mdDivider,
  },
}

const MDDivider = () => {
  return (
    <div
      style={{
        borderTop: '1px solid #d9d9d9',
        marginBottom: 16,
      }}
    ></div>
  )
}

const getArticleByPath = (category: string, article: string) => {
  return articles.find(
    (art) => art.articlePath == article && art.categoryPath == category
  )
}

export const getStaticProps = async ({ params, locale }) => {
  return {
    props: {
      category: params.articleCategory,
      article: params.article,
      ...(await serverSideTranslations(locale, [
        'common',
        'home',
        'authentication',
        'footer',
        'courses',
        'signUp',
        'home',
        'wishlist',
        'helpCentre',
      ])),
    },
  }
}

export const getStaticPaths = () => {
  const paths = articles.flatMap((art) =>
    ['en', 'uk'].map((ln) => ({
      params: { articleCategory: art.categoryPath, article: art.articlePath },
      locale: ln,
    }))
  )
  // const paths = articles.map((art) => ({
  //   params: {
  //     category: art.categoryPath,
  //     article: art.articlePath,
  //   },
  // }))
  return {
    paths,
    fallback: false,
  }
}

const URLParamsToTopicCategoriesMap = new Map<string, Category>([
  ['student', 'student'],
  ['author', 'author'],
  ['referral-partner', 'referralPartner'],
  ['legal', 'legal'],
])

const getTopicFromURLParam = (urlParam?: string): Category => {
  return URLParamsToTopicCategoriesMap.get(urlParam || '') || 'student'
}

const getURLParamFromTopic = (topic: Category) => {
  for (const [key, value] of URLParamsToTopicCategoriesMap.entries()) {
    if (value == topic) return key
  }
}

const HelpCentreArticle = () => {
  const { t } = useTranslation('helpCentre')

  const [isAtBottom, setIsAtBottom] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)

  const router = useRouter()

  const { articleCategory, article } = router.query

  const currentArticle = getArticleByPath(
    articleCategory as string,
    article as string
  )

  const [MDContent, setMDContent] = useState<RenderableTreeNode>()

  const { width } = useWindowDimensions()

  useEffect(() => {
    const mdPath = currentArticle?.MDpath
    // Guard: only fetch once we actually have a markdown path. Fetching with an
    // undefined path (before the article resolves) hits the SPA's 404 page,
    // whose raw HTML would then get parsed as markdown and dumped on screen.
    if (!mdPath) return

    let cancelled = false
    fetch(mdPath)
      .then((res) => {
        // Reject anything that isn't an actual markdown file — a missing file
        // returns the 404 HTML page (text/html), which must not be rendered.
        const contentType = res.headers.get('content-type') || ''
        if (!res.ok || contentType.includes('text/html')) {
          throw new Error('Markdown file not found')
        }
        return res.text()
      })
      .then((text) => {
        // Final safety net in case a CDN serves HTML with the wrong mime type.
        if (cancelled || /^\s*<(!doctype|html)/i.test(text)) return
        const ast = Markdoc.parse(
          text.replace(/\n\-\-\-\n/g, '\n{% mdDivider/ %}\n')
        )
        setMDContent(Markdoc.transform(ast, markDocConfig))
      })
      .catch(() => {
        if (!cancelled) setMDContent(undefined)
      })

    return () => {
      cancelled = true
    }
  }, [currentArticle?.MDpath])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY
      const mdContainer = document.getElementById('md-container')
      const mdContainerBottom = mdContainer
        ? mdContainer.getBoundingClientRect().bottom
        : 0
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      setIsAtBottom(
        scrolled + windowHeight >= documentHeight ||
          mdContainerBottom <= windowHeight
      )
      setShowBackToTop(scrolled > 200)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const buttonPosition = isAtBottom ? 'absolute' : 'fixed'

  return (
    <Layout displayHeaderAsHelpCentre={true}>
      <PageContainer>
        <PageContent>
          <RouteNavBar
            currentCategory={getTopicFromURLParam(articleCategory as string)}
            currentArticle={
              getArticleByPath(articleCategory as string, article as string)
                ?.articleName
            }
            setCategory={(x) => {
              router.push(
                {
                  pathname: `/help-centre/${getURLParamFromTopic(x)}`,
                },
                undefined,
                { shallow: true }
              )
            }}
          />
          <PageTitle>{t(currentArticle?.articleName as string)}</PageTitle>
          {currentArticle?.lastUpdated && (
            <LastUpdateCard>
              {t('lastUpdate') + ': ' + currentArticle.lastUpdated}
            </LastUpdateCard>
          )}
          <ArticleContent>
            <MDContainer id="md-container">
              {MDContent
                ? (Markdoc.renderers.react(
                    MDContent as RenderableTreeNode,
                    React,
                    {
                      components: {
                        MDDivider: MDDivider,
                      },
                    }
                  ) as ReactElement<any>)
                : null}
            </MDContainer>
            {width && width >= 1064 && currentArticle?.img}
          </ArticleContent>
        </PageContent>
        <NButton
          id="scroll-top-button"
          onClick={scrollToTop}
          className={`h-[50px] font-semiBold tracking-[0rem] text-[15px] ${
            showBackToTop ? 'opacity-100' : 'opacity-0'
          } transition-opacity duration-900`}
          color="primary"
          style={{
            padding: width && width < 1050 ? '8px 16px' : '12px 16px',
            position: buttonPosition,
            right: width && width < 1050 ? '16px' : '40px',
            bottom: buttonPosition === 'fixed' ? '60px' : '0px',
            zIndex: 999,
            minWidth: '3rem',
            gap: '0px',
          }}
        >
          {width && width < 1050 ? (
            <ArrowUpIcon />
          ) : (
            <>
              <ArrowUpIcon />
              {t('backToTop')}
            </>
          )}
        </NButton>
      </PageContainer>
    </Layout>
  )
}

export default HelpCentreArticle
